import React from 'react';
import { Specialists } from '../components/Specialists';
import { DownloadApp } from '../components/DownloadApp';
import { Award, Shield, Users, Globe, Target, Heart, Quote } from 'lucide-react';

export const About: React.FC = () => {
  return (
    <div className="pt-20">
      {/* Header */}
      <div className="bg-secondary text-white py-20 lg:py-28 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[80px] -translate-x-1/2 translate-y-1/2"></div>
        
        <div className="container mx-auto text-center relative z-10">
            <span className="inline-block px-3 py-1 bg-white/10 border border-white/10 rounded-full text-xs font-bold text-primary uppercase tracking-wider mb-4">Our Mission</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Redefining Men's <br/><span className="text-primary">Healthcare in Africa</span></h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              We are building the future of digital health—where access to world-class specialists is just a tap away, regardless of where you are.
            </p>
        </div>
      </div>

      {/* Official Mission Statement Section */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-6 max-w-5xl text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-secondary leading-snug md:leading-relaxed">
              Men’s Health Clinic Africa exists to make <span className="text-primary">quality, affordable healthcare</span> accessible to African men, breaking down barriers of cost, stigma, and distance through integrated clinics, telehealth, and data-driven care.
            </h2>
            <div className="w-24 h-1 bg-primary/20 mx-auto my-12 rounded-full"></div>
            <p className="text-xl md:text-2xl text-gray-600 font-medium leading-relaxed">
              We create a trusted space where men feel <span className="text-secondary font-bold">understood, supported, and empowered</span> to take charge of their health, so they can live longer, healthier lives and be present for their families and the communities that depend on them.
            </p>
        </div>
      </section>

      {/* Story Section */}
      <div className="py-24 bg-gray-50/50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
               <h2 className="text-3xl md:text-4xl font-bold text-secondary">Our Story</h2>
               <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                 <p>
                   MHC Africa was founded in 2025 with a simple yet powerful observation: men in Africa often delay seeking healthcare due to stigma, lack of access, or the inconvenience of traditional clinics.
                 </p>
                 <p>
                   We set out to change this narrative. By combining cutting-edge telemedicine technology with a network of empathetic, top-tier specialists, we've created a safe space where health is prioritized, privacy is guaranteed, and care is immediate.
                 </p>
                 <p>
                   Our goal is to reach 1 million men across the continent, proving that technology can bridge the gap between patients and the quality care they deserve.
                 </p>
               </div>
            </div>
            <div className="relative">
                <div className="absolute inset-0 bg-primary/10 rounded-[3rem] rotate-3 transform"></div>
                <img 
                  src="https://2bd20b7db6dd5db900dc830b78e06be9.cdn.bubble.io/f1765378640401x471645487489580350/32880.jpg?_gl=1*gxazr1*_gcl_au*OTYxMjAzODg4LjE3NjUzMTUwMDg.*_ga*Njg0NDczNjkxLjE3NjUzMTUwMDg.*_ga_BFPVR2DEE2*czE3NjUzMTUwMDgkbzEkZzEkdDE3NjUzNzE4NDYkajU0JGwwJGgw" 
                  alt="Medical Team Meeting" 
                  className="relative rounded-[3rem] shadow-2xl z-10 w-full object-cover aspect-[4/3]"
                />
            </div>
          </div>
        </div>
      </div>

      {/* Values Grid */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-6">
           <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-secondary">Why We Exist</h2>
              <p className="text-gray-500 mt-4">Our core values drive every decision we make.</p>
           </div>

           <div className="grid md:grid-cols-3 gap-8">
              {[
                  { icon: Shield, title: "Uncompromised Privacy", desc: "We believe healthcare should be private. Your data and consultations are sacred to us." },
                  { icon: Target, title: "Precision Care", desc: "No guesswork. We use data-driven diagnostics to provide accurate treatments." },
                  { icon: Heart, title: "Empathy First", desc: "We treat people, not just symptoms. Compassion is at the heart of our practice." },
                  { icon: Globe, title: "Universal Access", desc: "Quality healthcare shouldn't be a luxury. We make it accessible to everyone." },
                  { icon: Award, title: "Clinical Excellence", desc: "We only work with the top 1% of specialists in their respective fields." },
                  { icon: Users, title: "Community Focused", desc: "We are building a community where men support men in their health journeys." }
              ].map((item, i) => (
                  <div key={i} className="bg-gray-50 p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all">
                      <div className="w-12 h-12 bg-white text-primary rounded-xl flex items-center justify-center mb-6 shadow-sm">
                          <item.icon size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-secondary mb-3">{item.title}</h3>
                      <p className="text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
              ))}
           </div>
        </div>
      </div>

      <Specialists className="bg-gray-50" />

      <DownloadApp />
    </div>
  )
}